import React, { useState, useEffect } from 'react';

import { http } from '../../lib/http';

const LIMIT = 20;

const NewSignupsTab = ({ setMessage, setError, onSignupsChange }) => {

    const [signups, setSignups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showTodayOnly, setShowTodayOnly] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    const fetchSignups = async (pageArg = 1, append = false) => {
        if (append) setLoadingMore(true); else setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = { page: pageArg, limit: LIMIT };
            if (showTodayOnly) {
                params.today_only = 'true';
            } else {
                if (dateFrom) params.start_date = dateFrom;
                if (dateTo) params.end_date = dateTo;
            }

            const response = await http.get('/admin/signups', {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            const payload = response.data.data || response.data;
            const fetchedSignups = payload.signups || [];
            const pagination = payload.pagination || { total_count: fetchedSignups.length, has_next: false, page: pageArg };

            if (append) {
                setSignups(prev => [...prev, ...fetchedSignups]);
            } else {
                setSignups(fetchedSignups);
            }
            setPage(pagination.page || pageArg);
            setTotalCount(pagination.total_count || 0);
            setHasMore(!!pagination.has_next);

            if (onSignupsChange) onSignupsChange();

            const errors = payload.errors;
            if (errors && errors.length > 0) {
                setError(`⚠️ Warnings: ${errors.join('; ')}`);
            }

        } catch (error) {
            console.error('Error fetching signups:', error);
            const status = error.response?.status || 'No Status';
            const responseText = error.response?.data ? JSON.stringify(error.response.data).substring(0, 300) : 'No Response';
            const errorDetail = error.response?.data?.detail || error.message || 'Failed to fetch signups';
            setError(`❌ Fetch failed: ${errorDetail}\n🔗 URL: /admin/signups\n📊 Status: ${status}\n📄 Response: ${responseText}`);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Refetch (page 1) whenever filters change, including on initial mount
    useEffect(() => {
        fetchSignups(1, false);
    }, [showTodayOnly, dateFrom, dateTo]);

    const toggleTodayOnly = () => {
        const next = !showTodayOnly;
        if (next) {
            setDateFrom('');
            setDateTo('');
        }
        setShowTodayOnly(next);
    };

    const handleDateFromChange = (value) => {
        if (value && showTodayOnly) setShowTodayOnly(false);
        setDateFrom(value);
    };

    const handleDateToChange = (value) => {
        if (value && showTodayOnly) setShowTodayOnly(false);
        setDateTo(value);
    };

    const clearFilters = () => {
        setShowTodayOnly(false);
        setDateFrom('');
        setDateTo('');
    };

    const loadMore = () => {
        if (!loadingMore && hasMore) fetchSignups(page + 1, true);
    };

    if (loading) {
        return (
            <div className="bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-6 rounded-lg min-h-[400px]">
                <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-500 mx-auto mb-4"></div>
                    <p className="text-gray-700 text-base font-semibold">Loading signups...</p>
                </div>
            </div>
        );
    }

    const headerControls = (
        <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center space-x-2 bg-gray-800 p-2 rounded-lg border-2 border-gray-700">
                <span className="text-white text-sm font-semibold">Today Only:</span>
                <button
                    onClick={toggleTodayOnly}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${showTodayOnly ? 'bg-orange-500' : 'bg-gray-500'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showTodayOnly ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </div>
            <div className="flex items-center space-x-2 bg-gray-800 p-2 rounded-lg border-2 border-gray-700">
                <label className="text-white text-sm font-semibold">From:</label>
                <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => handleDateFromChange(e.target.value)}
                    className="bg-gray-900 text-white border border-gray-600 rounded px-2 py-1 text-sm"
                />
                <label className="text-white text-sm font-semibold">To:</label>
                <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => handleDateToChange(e.target.value)}
                    className="bg-gray-900 text-white border border-gray-600 rounded px-2 py-1 text-sm"
                />
                {(dateFrom || dateTo || showTodayOnly) && (
                    <button
                        onClick={clearFilters}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs font-semibold border border-gray-500"
                    >
                        Clear
                    </button>
                )}
            </div>
            <button
                onClick={() => fetchSignups(1, false)}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all"
            >
                Refresh
            </button>
        </div>
    );

    const headerBar = (
        <div className="bg-gradient-to-r from-black to-gray-900 rounded-xl p-6 shadow-lg border-2 border-orange-500">
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                    <h3 className="text-3xl font-bold text-white flex items-center gap-3">
                        Signups Report
                        <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-lg font-bold">
                            {signups.length} of {totalCount}
                        </span>
                    </h3>
                    <p className="text-gray-300 text-sm mt-2">View recently created customers via Stripe Checkouts or Admin Panel.</p>
                </div>
                {headerControls}
            </div>
        </div>
    );

    if (signups.length === 0) {
        return (
            <div className="space-y-6">
                {headerBar}
                <div className="bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-6 rounded-lg min-h-[300px]">
                    <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md border-2 border-orange-500">
                        <svg className="mx-auto h-16 w-16 text-orange-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Signups Found</h3>
                        <p className="text-gray-600 mb-6 text-sm">There are no signups matching your criteria.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {headerBar}

            {/* Signups List */}
            <div className="grid gap-4">
                {signups.map((signup) => {
                    if (!signup || !signup.data) return null;

                    return (
                        <div key={signup.id} className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-md hover:shadow-xl hover:border-orange-300 transition-all duration-300">
                            {/* Signup Header */}
                            <div className="flex justify-between items-start mb-4 pb-4 border-b-2 border-gray-100">
                                <div>
                                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                                        {signup.data['FIRST NAME:']} {signup.data['LAST NAME:']}
                                    </h4>
                                    <p className="text-gray-600 text-sm mb-3">{signup.data['Email Address']}</p>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-3 py-1 rounded-lg text-xs font-bold border-2 ${signup.data['SOURCE:'] === 'Stripe Checkout'
                                            ? 'bg-purple-50 text-purple-700 border-purple-300'
                                            : 'bg-green-50 text-green-700 border-green-300'
                                            }`}>
                                            {signup.data['SOURCE:']}
                                        </span>
                                        {signup.is_today && (
                                            <span className="px-3 py-1 rounded-lg text-xs font-bold border-2 bg-orange-50 text-orange-700 border-orange-300">
                                                Created Today
                                            </span>
                                        )}
                                        <span className="text-gray-500 text-xs font-medium">{signup.data['CREATED AT:']}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Signup Details */}
                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                                {/* Personal Information */}
                                <div className="bg-gradient-to-br from-gray-900 to-black rounded-lg p-4 border border-gray-700">
                                    <h5 className="font-bold text-white mb-3 text-sm flex items-center gap-2">
                                        <svg className="h-4 w-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Personal Information
                                    </h5>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-orange-500 font-semibold">Contact:</span>
                                            <span className="text-gray-300">{signup.data['CONTACT NO:']}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-orange-500 font-semibold">Address:</span>
                                            <span className="text-gray-300 mt-1">{signup.data['STREET NUMBER AND NAME:']}, {signup.data['CITY:']} {signup.data['POSTAL CODE:']}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-orange-500 font-semibold">Property:</span>
                                            <span className="text-gray-300">{signup.data['ITS A:']}</span>
                                        </div>
                                        {signup.data['SPECIFIC DELIVERY INSTRUCTIONS / BUZZ CODE:'] && (
                                            <div className="flex flex-col pt-2 border-t border-gray-700">
                                                <span className="text-orange-500 font-semibold">Instructions:</span>
                                                <span className="text-gray-300 mt-1">{signup.data['SPECIFIC DELIVERY INSTRUCTIONS / BUZZ CODE:']}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Plan Information */}
                                <div className="bg-gradient-to-br from-gray-900 to-black rounded-lg p-4 border border-gray-700">
                                    <h5 className="font-bold text-white mb-3 text-sm flex items-center gap-2">
                                        <svg className="h-4 w-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        Plan Details
                                    </h5>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex flex-col">
                                            <span className="text-orange-500 font-semibold">Plan:</span>
                                            <span className="text-gray-300 mt-1">{signup.data['MEAL PLAN TYPE:']} - {signup.data['MEAL PLAN TERM:']}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-orange-500 font-semibold">Term:</span>
                                            <span className="text-gray-300">{signup.data['PLAN TERM:']}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-orange-500 font-semibold">NV Preference:</span>
                                            <span className={!signup.data['Non-Veg Preference'] ? 'text-orange-400 font-bold' : 'text-gray-300'}>
                                                {signup.data['Non-Veg Preference'] || 'Missing (default will apply)'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-orange-500 font-semibold">Add-ons:</span>
                                            <span className="text-gray-300">{signup.data['ADD-ONS:'] || 'None'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-orange-500 font-semibold">Start Date:</span>
                                            <span className="text-gray-300">{signup.data['PLAN START DATE:']}</span>
                                        </div>
                                        <div className="flex flex-col pt-2 border-t border-gray-700">
                                            <span className="text-orange-500 font-semibold">E-Transfer From:</span>
                                            <span className="text-gray-300 mt-1">{signup.data['WE SHOULD EXPECT THE E-TRANSFER FROM (ID/NAME):']}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Special Instructions */}
                            {signup.data['ANY FOOD INSTRUCTIONS / ALLERGIES WE MUST KNOW ABOUT:'] && (
                                <div className="mt-4 pt-4 border-t-2 border-orange-500 bg-gradient-to-br from-gray-900 to-black rounded-lg p-4">
                                    <h5 className="font-semibold text-white text-sm mb-2">Food Instructions / Allergies</h5>
                                    <p className="text-sm text-gray-300">{signup.data['ANY FOOD INSTRUCTIONS / ALLERGIES WE MUST KNOW ABOUT:']}</p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {hasMore && (
                <div className="flex justify-center">
                    <button
                        onClick={loadMore}
                        disabled={loadingMore}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-60 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all"
                    >
                        {loadingMore ? 'Loading...' : `Load More (${Math.max(0, totalCount - signups.length)} remaining)`}
                    </button>
                </div>
            )}
        </div>
    );
};

export default NewSignupsTab;
